import { useRef, useCallback, useState } from "react";

const BRUSH_RADIUS = 24;
const BLUR_AMOUNT = 12;

export function useRedactionBrush() {
  const [isRedacting, setIsRedacting] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  /** Blurred regions: list of { x, y, r } for each stroke. */
  const regionsRef = useRef<Array<{ x: number; y: number; r: number }>>([]);

  const setCanvas = useCallback((canvas: HTMLCanvasElement | null) => {
    canvasRef.current = canvas;
  }, []);

  const setOverlay = useCallback((canvas: HTMLCanvasElement | null) => {
    overlayRef.current = canvas;
  }, []);

  /**
   * Apply a blur patch at (x, y) on the overlay canvas.
   * The overlay sits on top of the image; we draw a blurred copy of the region.
   */
  const addBlurAt = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      const overlay = overlayRef.current;
      if (!canvas || !overlay) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (clientX - rect.left) * scaleX;
      const y = (clientY - rect.top) * scaleY;
      const r = BRUSH_RADIUS * scaleX;

      const ctx = overlay.getContext("2d");
      if (!ctx) return;

      const offscreen = document.createElement("canvas");
      offscreen.width = r * 2;
      offscreen.height = r * 2;
      const offCtx = offscreen.getContext("2d");
      if (!offCtx) return;

      const src = canvas;
      offCtx.drawImage(
        src,
        x - r,
        y - r,
        r * 2,
        r * 2,
        0,
        0,
        r * 2,
        r * 2
      );
      offCtx.filter = `blur(${BLUR_AMOUNT}px)`;
      offCtx.drawImage(offscreen, 0, 0);
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(offscreen, 0, 0, r * 2, r * 2, x - r, y - r, r * 2, r * 2);
      ctx.restore();

      regionsRef.current.push({ x, y, r });
    },
    []
  );

  /**
   * Flatten overlay onto the image and return a new data URL (redacted image).
   */
  const getRedactedDataUrl = useCallback((): Promise<string | null> => {
    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    if (!canvas || !overlay) return Promise.resolve(null);

    const out = document.createElement("canvas");
    out.width = canvas.width;
    out.height = canvas.height;
    const ctx = out.getContext("2d");
    if (!ctx) return Promise.resolve(null);

    ctx.drawImage(canvas, 0, 0);
    ctx.drawImage(overlay, 0, 0);
    return Promise.resolve(out.toDataURL("image/png"));
  }, []);

  const clearOverlay = useCallback(() => {
    const overlay = overlayRef.current;
    if (overlay) {
      const ctx = overlay.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, overlay.width, overlay.height);
    }
    regionsRef.current = [];
  }, []);

  return {
    isRedacting,
    setIsRedacting,
    setCanvas,
    setOverlay,
    addBlurAt,
    getRedactedDataUrl,
    clearOverlay,
    brushRadius: BRUSH_RADIUS,
  };
}
